import React, {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useSearchParams} from 'react-router-dom'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {ROUTES} from '../../../../_gori/constants'
import AuthService from '../core/_requests'
import {VerifyInviteTokenResponse} from '../core/_models'
import {RegistrationForm} from './RegistrationForm'

export function Registration() {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const invite = searchParams.get('invite')
  const {newCancelToken, isCancel} = useCancelToken()
  const [inviteInfo, setInviteInfo] = useState<VerifyInviteTokenResponse | null>(null)
  const [verifyingInvite, setVerifyingInvite] = useState(!!invite)

  const verifyInvite = useCallback(async () => {
    if (!invite) {
      setInviteInfo(null)
      setVerifyingInvite(false)
      return
    }

    try {
      setVerifyingInvite(true)
      const response = (await AuthService.verifyInviteToken(
        {token: invite},
        {cancelToken: newCancelToken()}
      )) as VerifyInviteTokenResponse
      setInviteInfo(response)
    } catch (error: any) {
      if (isCancel(error)) return
      setInviteInfo(null)
    } finally {
      setVerifyingInvite(false)
    }
  }, [invite, newCancelToken, isCancel])

  useEffect(() => {
    verifyInvite()
  }, [verifyInvite])

  const hasInvite = !!invite
  const showInvalidInvite = hasInvite && !verifyingInvite && !inviteInfo
  const showVerifyingInvite = hasInvite && verifyingInvite
  const invalidInviteMessage = intl.formatMessage({id: 'INVALID_REGISTRATION_LINK'})

  return (
    <>
      {showVerifyingInvite && (
        <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='text-center py-10'>
            <div className='spinner-border spinner-custom border-5' role='status' />
          </div>
        </div>
      )}

      {!showVerifyingInvite && inviteInfo && !showInvalidInvite && (
        <div
          className='d-flex justify-content-center text-center invite-message fw-bold fs-4'
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              {id: 'MANAGER_INVITATION_MESSAGE'},
              {organization_name: inviteInfo.company_name}
            ),
          }}
        />
      )}

      {!showVerifyingInvite && showInvalidInvite ? (
        <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
            <div className='mb-10 text-center'>
              <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'CREATE_AN_ACCOUNT'})}</h1>
            </div>
            <div className='mb-10 alert alert-danger alert-dismissible'>
              <div className='alert-text'>{invalidInviteMessage}</div>
            </div>
            <div className='d-flex flex-wrap justify-content-center gap-4 pb-lg-0'>
              <Link to={ROUTES.AUTH.LOGIN} className='link-primary fw-bolder'>
                <button type='button' className='btn btn-lg btn-advanced-primary'>
                  {intl.formatMessage({id: 'LOGIN'})}
                </button>
              </Link>
            </div>
          </div>
        </div>
      ) : !showVerifyingInvite ? (
        <RegistrationForm inviteToken={invite} />
      ) : null}
    </>
  )
}
